import { z } from 'zod';
import { stringArraySchema, timestampSchema } from './shared';

/**
 * Specialist advisory landing pages — one row per route, one template.
 *
 * Field order mirrors `reusable_components.service_page_order` from the
 * enhancement spec, which is also the render order in `ServicePage.tsx`:
 * label/audience → h1 → intro → bestWhen → scope → exclusions →
 * engagementModel → evidence → faq → single CTA.
 *
 * `exclusions` is deliberately NOT optional at the row level. The spec makes
 * scope-and-limitations mandatory on these pages ("New specialist pages state
 * scope and limitations and do not imply legal, regulatory,
 * security-certification, investment, or procurement authority"), so an empty
 * array has to be a conscious choice rather than a missing key.
 */
export const servicePageFaqItemSchema = z.object({
  q: z.string().min(1),
  a: z.string().min(1),
});

export const servicePageFaqSchema = z.array(servicePageFaqItemSchema);

/**
 * `claim_status` tracks how much evidence stands behind a page's copy, per the
 * spec's `publishing_guardrails.claim_status_values`. Everything seeded in
 * Phase 2 is `proposed_positioning` — authored positioning the owner has not
 * yet signed off.
 */
export const claimStatusSchema = z.enum([
  'verified_by_owner',
  'published_unverified',
  'proposed_positioning',
  'remove_pending_review',
]);

export const servicePageRowSchema = z.object({
  slug: z.string().min(1),
  label: z.string().min(1),
  audience: z.string(),
  h1: z.string().min(1),
  intro: z.string(),
  bestWhen: stringArraySchema,
  scope: stringArraySchema,
  exclusions: stringArraySchema,
  engagementModel: z.string().nullable(),
  faq: servicePageFaqSchema,
  ctaLabel: z.string().nullable(),
  ctaHref: z.string().nullable(),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  evidenceProjectSlugs: stringArraySchema,
  evidenceTestimonialIds: stringArraySchema,
  claimStatus: claimStatusSchema,
  published: z.boolean(),
  sortOrder: z.number().int(),
  updatedAt: timestampSchema,
});

export const servicePageInsertSchema = z.object({
  slug: z.string().min(1),
  label: z.string().min(1),
  audience: z.string(),
  h1: z.string().min(1),
  intro: z.string(),
  bestWhen: stringArraySchema.optional(),
  scope: stringArraySchema.optional(),
  exclusions: stringArraySchema.optional(),
  engagementModel: z.string().nullable().optional(),
  faq: servicePageFaqSchema.optional(),
  ctaLabel: z.string().nullable().optional(),
  ctaHref: z.string().nullable().optional(),
  seoTitle: z.string().nullable().optional(),
  seoDescription: z.string().nullable().optional(),
  evidenceProjectSlugs: stringArraySchema.optional(),
  evidenceTestimonialIds: stringArraySchema.optional(),
  claimStatus: claimStatusSchema.optional(),
  published: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

export const servicePageUpdateSchema = servicePageInsertSchema.partial();

export type ServicePageFaqItem = z.infer<typeof servicePageFaqItemSchema>;
export type ClaimStatus = z.infer<typeof claimStatusSchema>;
export type ServicePageRow = z.infer<typeof servicePageRowSchema>;
export type ServicePageInsert = z.input<typeof servicePageInsertSchema>;
export type ServicePageUpdate = z.input<typeof servicePageUpdateSchema>;
